import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { SessionStorageService } from '../../services/session-storage.service';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { EnvironmentService } from '../../services/environment.service';
import { ErrorReportingService } from '../../services/error-reporting.service';
import { ApiResponse, GridResults } from '../../models/general-models';
import { FundSearch, Template } from '../../models/account-models';
import {
  Family,
  FiveTwoNine,
  FundDetail,
  FundInfo,
  HoldingsDetail,
} from '../../models/iw-models';
import { GroupProfile } from '../../models/global.model';

@Injectable({
  providedIn: 'root',
})
export class InvestmentWizardService {
  private http = inject(HttpClient);
  private ss = inject(SessionStorageService);
  private rbs = inject(RightBridgeApiService);
  private envSvc = inject(EnvironmentService);
  private errSvc = inject(ErrorReportingService);

  private environment;
  private baseUrl: string;
  private firm: string = this.ss.get('firm') || null;
  private org: string;

  constructor() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    this.org = this.environment.org;
    this.firm = this.environment.firm || '';
  }

  getTemplates(): Observable<
    ApiResponse<{
      UserTemplates: Template[];
      FirmTemplates: Template[];
    }>
  > {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'iw')
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Investment/Templates`;

    return this.http
      .get<
        ApiResponse<{
          UserTemplates: Template[];
          FirmTemplates: Template[];
        }>
      >(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  fundFamilyList(
    programId?: string | number
  ): Observable<{ results: Family[] }> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'iw')
      .set('X-RB-O', this.org);
    this.firm = this.rbs.setFirmId(null, true);
    let url = `${this.baseUrl}/Investment/FundFamilies?firmId=${this.firm}`;

    if (programId) {
      url = url + `&programId=${programId}`;
    }

    return this.http
      .get<{ results: Family[] }>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  fundSearch(
    name: string | number,
    program?: string | number,
    fams?: any,
    share?: string | number,
    selectionType?: string | number,
    fiveTwoNine?: string | number
  ): Observable<ApiResponse<FundSearch[]>> {
    const sess = this.ss.get('session'),
      reqHeaders = new HttpHeaders()
        .set('X-RB-S', sess)
        .set('X-RB-I', 'iw')
        .set('X-RB-O', this.org);

    let includeFirm = false;

    if (this.ss.get('currentApp')?.toLowerCase() !== 'pp') {
      this.firm = this.rbs.setFirmId(null, true);
      includeFirm = true;
    }

    let url = `${this.baseUrl}/Investment/Symbol`;

    let isFirstParam = true;

    if (includeFirm) {
      url += `?firmId=${this.firm}`;
      isFirstParam = false;
    }

    if (name) {
      url += `${isFirstParam ? '?' : '&'}searchString=${name}`;
      isFirstParam = false;
    }

    if (program) {
      url += `${isFirstParam ? '?' : '&'}programId=${program}`;
      isFirstParam = false;
    }

    if (fams) {
      url += `${isFirstParam ? '?' : '&'}fundFamilyIds={'fundFamilyIds': ['${fams}'] }`;
      isFirstParam = false;
    }

    if (share) {
      url += `${isFirstParam ? '?' : '&'}shareClass=${share}`;
      isFirstParam = false;
    }

    if (fiveTwoNine) {
      url += `${isFirstParam ? '?' : '&'}planID=${fiveTwoNine}`;
      isFirstParam = false;
    }

    return this.rbs.getRequest<ApiResponse<FundSearch[]>>(url, reqHeaders);
  }

  fundInfo(
    funds: any,
    sendFirmId = false
  ): Observable<ApiResponse<FundInfo[]>> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'iw')
      .set('X-RB-O', this.org);
    this.firm = this.rbs.setFirmId(null, true);
    let url = `${this.baseUrl}/Investment/FundInfo?symbols=${encodeURIComponent(funds)}`;

    if (sendFirmId) {
      url += `&firmId=${this.firm}`;
    }

    return this.http
      .get<ApiResponse<FundInfo[]>>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  fundDetails(symbol: string | number): Observable<ApiResponse<FundDetail[]>> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'iw')
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Investment/FundDetails?symbol=${symbol}`;

    return this.http
      .get<ApiResponse<FundDetail[]>>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getMultiFunds(
    program,
    fams,
    share
  ): Observable<ApiResponse<{ FundName: string; FundID: string }[]>> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'iw')
      .set('X-RB-O', this.org);
    this.firm = this.rbs.setFirmId(null, true);
    let url = `${this.baseUrl}/Investment/AssetAllocationFunds?firmId=${this.firm}&programId=${program}`;

    if (fams) {
      url += `&fundFamilyIds={'fundFamilyIds': [${fams}] }`;
    }

    if (share) {
      url += `&shareClass=${share}`;
    }

    return this.http
      .get<ApiResponse<{ FundName: string; FundID: string }[]>>(url, {
        headers: reqHeaders,
      })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  get529(firmId = false, programId = null): Observable<FiveTwoNine[]> {
    const sess = this.ss.get('session'),
      reqHeaders = new HttpHeaders()
        .set('X-RB-S', sess)
        .set('X-RB-I', 'iw')
        .set('X-RB-O', this.org);
    let url = `${this.baseUrl}/Investment/Get529Plans`;
    let firmToUse;

    if (firmId) {
      if (this.firm && this.firm !== this.ss.get('firm')) {
        firmToUse = this.ss.get('firm');
      } else {
        firmToUse =
          this.firm && this.firm.length
            ? this.firm
            : this.rbs.setFirmId(null, true);
      }
    }

    if (firmId && programId) {
      url = url + `?FirmID=${firmToUse}&ProgramID=${programId}`;
    }

    return this.rbs.getRequest<FiveTwoNine[]>(url, reqHeaders);
  }

  getReport(
    profile: string | number,
    details: boolean,
    alternatives?: boolean,
    alternativesChart?: boolean,
    silent = false
  ): Observable<Blob | null> {
    const id = this.ss.get('session');
    this.firm =
      this.firm && this.firm.length ? this.firm : this.rbs.setFirmId();
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'iw')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');

    let url = `${this.baseUrl}/reports/iw/${profile}?firmId=${this.firm}`;

    if (details && !!details) {
      url += '&includeDetail=true';
    }

    if (alternatives && !!alternatives) {
      url += '&includeAlternatives=true';
    }

    if (alternativesChart && !!alternativesChart) {
      url += '&includeFundAlternativesChart=true';
    }

    if (silent) {
      url += '&returnReport=false';
    }

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getDashFundCompareReport(symbol, program, number?): Observable<Blob | null> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'iw')
      .set('X-RB-O', this.org);
    this.firm = this.rbs.setFirmId(null, true);
    let url = `${this.baseUrl}/Reports/FundCompare?firmID=${this.firm}&program=${program}&symbol=${symbol}`;

    if (number) {
      url += `&numFunds=${number}`;
    }

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getFundCompareReport(
    symbol,
    program,
    horizon,
    amount,
    alts?,
    family?
  ): Observable<Blob | null> {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'iw')
      .set('X-RB-O', this.org);
    this.firm = this.rbs.setFirmId(null, true);
    let url = `${this.baseUrl}/Reports/FundCompare?firmID=${this.firm}&program=${program}&symbol=${symbol}&InvestmentAmount=${amount}&TimeHorizon=${horizon}`;

    if (alts) {
      url += `&numFunds=${alts}`;
    }

    if (family) {
      url += `&CurrentlyOwnedFundFamilies=${family}`;
    }

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getPrograms(profile: string): Observable<GroupProfile> {
    return this.rbs.getGroupProfile(profile, 'ProcessFundCategories', 'iw');
  }

  getModels(profile: string): Observable<GroupProfile> {
    return this.rbs.getGroupProfile(profile, 'ProcessModel', 'iw');
  }

  getFundCats(profile: string): Observable<GroupProfile> {
    return this.rbs.getGroupProfile(profile, 'SelectFunds', 'iw');
  }

  processPortfolio(profile: string): Observable<GroupProfile> {
    const globalFirm = this.ss.get('globals').firmId;
    // We're forcing the firm to the root firm for this call because it broke allstate.
    // The method for setting the firm in rbs was built around the security mutual SSO
    // and works except for producing home office users that have their rep code attached
    // to another unit.
    this.rbs.setApp('iw');
    return this.rbs.getRequest<GroupProfile>(
      `${this.rbs.baseUrl}/Process/Portfolio/${profile}?firmId=${globalFirm}`,
      null,
      'iw'
    );
  }

  getProductGridSetup(firmId: string): Observable<GridResults> {
    return this.rbs.getRequest<GridResults>(
      `${this.rbs.baseUrl}/investment/GridSetup?firmid=${firmId}`,
      null,
      'iw'
    );
  }

  getProductGrid(
    reqData
  ): Observable<Blob | HttpResponse<ApiResponse<GridResults>>> {
    let url = `${this.rbs.baseUrl}/Investment/ProductGrid`;
    if (reqData.queryData) {
      const queryData = encodeURIComponent(JSON.stringify(reqData.queryData));
      url = url + `?query=${queryData}`;
    }
    if (reqData.resType === 'application/csv') {
      const reqHeaders = new HttpHeaders()
        .set('X-RB-S', this.ss.get('session'))
        .set('X-RB-I', 'iw')
        .set('X-RB-O', this.org)
        .set('Content-Type', 'application/csv')
        .set('Accept', 'application/csv');

      return this.http
        .get(url, {
          headers: reqHeaders,
          responseType: 'blob',
        })
        .pipe(
          map(res => {
            return res;
          }),
          catchError((error: any) => {
            return this.errSvc.handleError(error);
          })
        );
    }
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', this.ss.get('session'))
      .set('X-RB-I', 'iw')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/json');
    return this.rbs.getRequest<HttpResponse<ApiResponse<GridResults>>>(
      url,
      reqHeaders
    );
  }

  processAlternative(data): Observable<GroupProfile> {
    return this.rbs.patchGroupProfile(data, 'SelectAlternative', null, 'iw');
  }

  uploadHoldingsFile(fileData): Observable<HoldingsDetail> {
    const firmId = this.ss.get('globals').firmId;

    return this.rbs.postRequest<HoldingsDetail>(
      `${this.rbs.baseUrl}/Investment/GetBulkSymbol?firmid=${firmId}`,
      fileData
    );
  }

  getProspectus(ticker): Observable<{
    data: {
      Ticker: string;
      CIK: string;
      ProspectusUrl: string;
    };
  }> {
    return this.rbs.getRequest<{
      data: {
        Ticker: string;
        CIK: string;
        ProspectusUrl: string;
      };
    }>(`${this.rbs.baseUrl}/Investment/Prospectus?ticker=${ticker}`);
  }
}
